import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { onSessionExpired, setAccessToken } from '../api/client'
import { api } from '../api/endpoints'
import { AuthContext, ROLE_RANK } from '../hooks/auth-context'
import type { AuthState } from '../hooks/auth-context'
import type { Profile } from '../types'

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  // At start-up there is no access token in memory -- by design, it is never
  // written to disk. If the refresh cookie is still valid, /api/me succeeds
  // after the client's silent renewal: that is how a session survives an F5.
  useEffect(() => {
    let active = true
    const controller = new AbortController()

    api
      .me(controller.signal)
      .then((p) => {
        if (active) setProfile(p)
      })
      .catch(() => {
        if (active) setProfile(null)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
      controller.abort()
    }
  }, [])

  // When even the refresh fails the client calls back here. Dropping the query
  // cache as well: what it holds belongs to a session that no longer exists.
  useEffect(() => {
    onSessionExpired(() => {
      setProfile(null)
      queryClient.clear()
    })
    return () => onSessionExpired(null)
  }, [queryClient])

  const login = useCallback(async (email: string, password: string, totp?: string) => {
    const token = await api.login(email, password, totp)
    setAccessToken(token.access_token)
    setProfile(await api.me())
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.logout()
    } finally {
      setAccessToken(null)
      setProfile(null)
      queryClient.clear()
    }
  }, [queryClient])

  const value = useMemo<AuthState>(
    () => ({
      profile,
      loading,
      login,
      logout,
      hasRole: (role) => (profile ? ROLE_RANK[profile.role] >= ROLE_RANK[role] : false),
    }),
    [profile, loading, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
